import type React from 'react';
import { useRef } from 'react';
import { toast } from 'react-toastify';
import { UrlAvailabilityStatus } from './UrlAvailabilityStatus';
import styles from './Specifications.module.css';

interface PostmanEnvConfigProps {
  postmanEnv: any;
  loading: boolean;
  onUploadEnvironment: (parsed: any) => Promise<void>;
  checking?: boolean;
  checkProgress?: { checked: number; total: number } | null;
  checkResult?: 'available' | 'unavailable' | null;
  onCheckAvailability?: () => void;
}

export const PostmanEnvConfig: React.FC<PostmanEnvConfigProps> = ({
  postmanEnv,
  loading,
  onUploadEnvironment,
  checking,
  checkProgress,
  checkResult,
  onCheckAvailability,
}) => {
  const envFileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    envFileInputRef.current?.click();
  };

  const onEnvFileSelected = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await onUploadEnvironment(parsed);
    } catch {
      toast.error('Invalid JSON file');
    }
  };

  return (
    <div className={styles.configCard}>
      <div className={styles.configHeader}>
        <h3>Postman Environment</h3>
        <p>
          {postmanEnv
            ? 'Environment file is configured. You can replace it by uploading a new one.'
            : 'Upload a Postman environment file (.json) to configure environment variables.'}
        </p>
      </div>

      {postmanEnv && (
        <div style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
          <div>
            <strong>Name:</strong> {postmanEnv.name || 'N/A'}
          </div>
          <div>
            <strong>Variables:</strong> {postmanEnv.values?.length || 0}
          </div>
          {postmanEnv._postman_variable_scope && (
            <div>
              <strong>Scope:</strong> {postmanEnv._postman_variable_scope}
            </div>
          )}
        </div>
      )}

      {postmanEnv?.values && postmanEnv.values.length > 0 && (
        <div className={styles.envVariablesList}>
          {postmanEnv.values.map((variable: any, index: number) => (
            <div className={styles.envVariable} key={index}>
              <span className={styles.envKey}>{variable.key}</span>
              <span className={styles.envValue}>{variable.value || '(empty)'}</span>
            </div>
          ))}
        </div>
      )}

      <UrlAvailabilityStatus
        checking={checking}
        checkProgress={checkProgress}
        checkResult={checkResult}
        onCheckAvailability={postmanEnv ? onCheckAvailability : undefined}
      />

      <div className={styles.urlCheckActions}>
        <button className={styles.button} onClick={handleUploadClick} disabled={loading}>
          {postmanEnv ? 'Replace Environment' : 'Upload Environment'}
        </button>
      </div>

      <input
        className={styles.hiddenInput}
        type="file"
        accept=".json"
        ref={envFileInputRef}
        onChange={onEnvFileSelected}
      />
    </div>
  );
};
